// src/AuthContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import api, { BASE_DOMAIN } from "./axiosConfig";
import { initializeEcho } from "./bootstrap"; // ensure bootstrap exports initializeEcho()

export const AuthContext = createContext({
  user: null,
  loading: true,
  echo: null,
  login: async () => ({}),
  logout: async () => {},
  refreshUser: async () => null,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const echoRef = useRef(null);
  const reqIdRef = useRef(0); // prevents older responses overwriting newer state

  // ---- Helpers ----
  const extractUser = useCallback((payload) => {
    if (!payload) return null;

    // common shapes
    if (payload.user && payload.user.id !== undefined) return payload.user;
    if (payload.data && payload.data.id !== undefined) return payload.data;

    // direct user object
    if (payload.id !== undefined) return payload;

    return null;
  }, []);

  const normalizeRole = useCallback((roleRaw) => {
    if (!roleRaw) return null;
    const r = String(roleRaw).toLowerCase().trim();

    const roleMap = {
      "marketing-access": "marketing",
      "consultant-access": "consultant",
      "admin-access": "admin",
      "super_admin": "superadmin",
      "super-admin": "superadmin",
      "super admin": "superadmin",
      marketing: "marketing",
      consultant: "consultant",
      admin: "admin",
      superadmin: "superadmin",
    };

    return roleMap[r] ?? r;
  }, []);

  const shapeUser = useCallback(
    (backendUser) => {
      if (!backendUser || typeof backendUser.id === "undefined") return null;

      return {
        ...backendUser,
        uid: String(backendUser.id),
        role: normalizeRole(backendUser.role),
      };
    },
    [normalizeRole]
  );

  const ensureEchoForUser = useCallback((u) => {
    if (u && !echoRef.current) {
      try {
        echoRef.current = initializeEcho();
      } catch (error) {
        console.warn("Echo initialization skipped:", error?.message || error);
        echoRef.current = null;
      }
    }
    if (!u && echoRef.current) {
      try {
        echoRef.current.disconnect();
      } catch (_) {
        // ignore
      }
      echoRef.current = null;
    }
  }, []);

  // ---- Core Sanctum SPA flow ----
  const primeCsrfCookie = useCallback(async () => {
    // CSRF cookie endpoint is NOT under /api
    await api.get(`/sanctum/csrf-cookie?_=${Date.now()}`, {
      baseURL: BASE_DOMAIN,
      withCredentials: true,
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  }, []);

  /**
   * Fetch authenticated user:
   * - Your API has GET /api/user protected by auth:sanctum
   * - axios baseURL is .../api so we call "/user"
   */
  const refreshUser = useCallback(async () => {
    const myReqId = ++reqIdRef.current;

    try {
      const res = await api.get("/user"); // -> GET {BASE}/api/user
      const raw = extractUser(res.data);
      const u = shapeUser(raw);

      if (reqIdRef.current === myReqId) {
        setUser(u);
        ensureEchoForUser(u);
      }

      return u;
    } catch (err) {
      if (reqIdRef.current === myReqId) {
        setUser(null);
        ensureEchoForUser(null);
      }
      return null;
    }
  }, [ensureEchoForUser, extractUser, shapeUser]);

  /**
   * Login:
   * Your backend route is POST /api/login (in routes/api.php)
   * So we call api.post("/login") which becomes {BASE}/api/login
   * Prime CSRF cookie first.
   */
  const login = useCallback(
    async (credentials) => {
      try {
        await primeCsrfCookie();

        const res = await api.post("/login", credentials); // -> POST {BASE}/api/login
        const raw = extractUser(res.data);
        const u = shapeUser(raw);

        // If your login endpoint does NOT return user, fall back to /user
        if (!u) {
          const refreshed = await refreshUser();
          if (!refreshed) throw new Error("Login succeeded but user fetch failed.");
          return refreshed;
        }

        setUser(u);
        ensureEchoForUser(u);
        return u;
      } catch (error) {
        setUser(null);
        ensureEchoForUser(null);
        throw error;
      }
    },
    [ensureEchoForUser, extractUser, primeCsrfCookie, refreshUser, shapeUser]
  );

  /**
   * Logout:
   * Your backend route is POST /api/logout (in routes/api.php, auth:sanctum)
   */
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await api.post("/logout"); // -> POST {BASE}/api/logout
    } catch (err) {
      // continue with client-side logout anyway
      console.error("Logout API call failed (continuing):", err);
    } finally {
      setUser(null);
      ensureEchoForUser(null);
      setLoading(false);
    }
  }, [ensureEchoForUser]);

  // On app load: check if already logged in (skip for public pages)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const publicPaths = [
        "/new-inquiry",
        "/service-provider-inquiry",
        "/r/reply",
        "/meta/embedded-signup/callback",
      ];
      const isPublicPage = publicPaths.some((path) =>
        window.location.pathname.startsWith(path)
      );

      if (isPublicPage) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);

      // Optional but helpful: prime CSRF so subsequent POSTs work immediately
      try {
        await primeCsrfCookie();
      } catch (_) {
        // ignore
      }

      await refreshUser();
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [primeCsrfCookie, refreshUser]);

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      echo: echoRef.current,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
